import {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

function sessionSourceTagKey(funnelSlug: string, sourceId: string): string {
  return `${funnelSlug}\t${sourceId}`;
}

type Ctx = {
  registerSessionSourceTag: (
    funnelSlug: string,
    sourceId: string,
    tagId: string
  ) => void;
  unregisterSessionSourceTag: (
    funnelSlug: string,
    sourceId: string,
    tagId: string
  ) => void;
  isSessionSourceTag: (
    funnelSlug: string,
    sourceId: string,
    tagId: string
  ) => boolean;
};

const PopupImportSessionSourceTagsContext = createContext<Ctx | null>(null);

export const PopupImportSessionSourceTagsProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const [sessionTagsByKey, setSessionTagsByKey] = useState<
    Map<string, Set<string>>
  >(() => new Map());

  const registerSessionSourceTag = useCallback(
    (funnelSlug: string, sourceId: string, tagId: string) => {
      const key = sessionSourceTagKey(funnelSlug, sourceId);
      setSessionTagsByKey((prev) => {
        const next = new Map(prev);
        const set = new Set(next.get(key) ?? []);
        set.add(tagId);
        next.set(key, set);
        return next;
      });
    },
    []
  );

  const unregisterSessionSourceTag = useCallback(
    (funnelSlug: string, sourceId: string, tagId: string) => {
      const key = sessionSourceTagKey(funnelSlug, sourceId);
      setSessionTagsByKey((prev) => {
        const existing = prev.get(key);
        if (!existing?.has(tagId)) return prev;
        const next = new Map(prev);
        const set = new Set(existing);
        set.delete(tagId);
        if (set.size === 0) {
          next.delete(key);
        } else {
          next.set(key, set);
        }
        return next;
      });
    },
    []
  );

  const isSessionSourceTag = useCallback(
    (funnelSlug: string, sourceId: string, tagId: string) => {
      const key = sessionSourceTagKey(funnelSlug, sourceId);
      return sessionTagsByKey.get(key)?.has(tagId) ?? false;
    },
    [sessionTagsByKey]
  );

  const value = useMemo(
    () => ({
      registerSessionSourceTag,
      unregisterSessionSourceTag,
      isSessionSourceTag,
    }),
    [isSessionSourceTag, registerSessionSourceTag, unregisterSessionSourceTag]
  );

  return (
    <PopupImportSessionSourceTagsContext.Provider value={value}>
      {children}
    </PopupImportSessionSourceTagsContext.Provider>
  );
};

export function usePopupImportSessionSourceTags(): Ctx {
  const v = useContext(PopupImportSessionSourceTagsContext);
  if (!v) {
    throw new Error(
      "usePopupImportSessionSourceTags must be used within PopupImportSessionSourceTagsProvider"
    );
  }
  return v;
}
